/**
 * watch-configurator.js
 * ---------------------------------------------------------------------------
 * Custom element <time-watch-configurator>. Orquesta todo el configurador:
 * estado, preview por capas, tabs de categorías, grilla de opciones,
 * resumen, modal de confirmación y el flujo de pedido por WhatsApp.
 *
 * Se carga como módulo ES (<script type="module">) desde
 * sections/watch-configurator.liquid. No depende de ningún framework.
 *
 * FUENTE DE DATOS: el catálogo (modelo/categorías/componentes/settings) se
 * lee de un <script type="application/json" data-watch-configurator-catalog>
 * ubicado dentro de este mismo elemento, que la sección Liquid rellena a
 * partir de metaobjects + theme settings. Si ese script no existe o no es
 * JSON válido (por ejemplo, abriendo este componente fuera de Shopify para
 * desarrollo local), se usa el dataset de ejemplo como fallback -- así el
 * resto del código nunca necesita saber de dónde vinieron los datos.
 */

import { CompatibilityEngine } from "./watch-configurator-compatibility.js";
import { LayerImagePreloader } from "./watch-configurator-preloader.js";
import { WatchLayerCompositor } from "./watch-configurator-canvas.js";
import { formatPrice } from "./watch-configurator-money.js";
import {
  generateConfigurationId,
  buildWhatsAppMessage,
  buildConfigurationRecord,
  sendConfigurationToWhatsApp,
} from "./watch-configurator-whatsapp.js";
import { trackEvent } from "./watch-configurator-analytics.js";
import { SAMPLE_CATALOG } from "./watch-configurator-sample-data.js";

const DEPOSIT_PERCENTAGE = 0.3;

const STATUS_LABELS = {
  low_stock: "Pocas unidades",
  preorder: "Disponible por pedido",
  out_of_stock: "Agotado",
  disabled: "No disponible",
};

const RESUMEN_TAB_ID = "__resumen__";
const MODEL_TAB_ID = "__modelo__";

function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === "class") node.className = value;
    else if (key === "html") node.innerHTML = value;
    else if (key.startsWith("on") && typeof value === "function") node.addEventListener(key.slice(2), value);
    else if (value !== null && value !== undefined && value !== false) node.setAttribute(key, value === true ? "" : value);
  }
  for (const child of [].concat(children)) {
    if (child == null) continue;
    node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return node;
}

export class TimeWatchConfigurator extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.catalog = this._loadCatalog();
    this.settings = this.catalog.settings || {};
    this.engine = new CompatibilityEngine(this.catalog);
    this.preloader = new LayerImagePreloader();
    this.compositor = new WatchLayerCompositor(this.preloader);
    this.componentsById = new Map(this.catalog.components.map((c) => [c.id, c]));
    this.categoriesById = new Map(this.catalog.categories.map((c) => [c.id, c]));

    this.model = this._pickModel();
    this.selections = {};
    this.messages = [];
    this.activeTab = MODEL_TAB_ID;
    this._configurationCompletedFired = false;

    this._applyDefaultSelectionsForModel();
    this._renderShell();
    this._renderAll();
    this._preloadInitialAssets();

    trackEvent("configurator_started", { modelId: this.model ? this.model.id : null });
  }

  // ---------------------------------------------------------------- DATA --

  _loadCatalog() {
    const dataEl = this.querySelector("script[data-watch-configurator-catalog]");
    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent);
        if (parsed && Array.isArray(parsed.components) && parsed.components.length) return parsed;
      } catch (err) {
        console.warn("[TIME Configurator] Catálogo inválido, se usa el set de datos de ejemplo.", err);
      }
    }
    return SAMPLE_CATALOG;
  }

  _pickModel() {
    const models = (this.catalog.models || []).filter((m) => m.active !== false);
    const requestedId = this.getAttribute("data-default-model");
    return models.find((m) => m.id === requestedId) || models[0] || null;
  }

  _modelCategories() {
    if (!this.model) return [];
    // El ORDEN de `model.categoryIds` manda (es el orden en que el
    // administrador acomodó la lista de categorías del modelo en el
    // metaobject, en Shopify Admin -> Contenido). `category.stepOrder` sólo
    // se usa como respaldo si el catálogo no trae un orden explícito por
    // modelo (por ejemplo, datasets construidos a mano).
    return this.model.categoryIds.map((id) => this.categoriesById.get(id)).filter(Boolean);
  }

  _modelComponents() {
    if (!this.model) return [];
    return this.catalog.components.filter((c) => c.modelIds.includes(this.model.id));
  }

  _applyDefaultSelectionsForModel() {
    this.selections = {};
    const modelComponents = this._modelComponents();
    for (const category of this._modelCategories()) {
      const options = this.engine.getAvailableComponents(category.id, this.selections, modelComponents);
      const firstSelectable = options.find((o) => o.selectable);
      if (firstSelectable) {
        const result = this.engine.applySelection(this.selections, firstSelectable.component.id, modelComponents);
        this.selections = result.selections;
      }
    }
  }

  // -------------------------------------------------------------- RENDER --

  _renderShell() {
    this.innerHTML = "";
    this.classList.add("twc");

    this.previewEl = el("div", { class: "twc-preview", role: "img", "aria-label": "Vista previa de tu reloj personalizado" });
    this.priceEl = el("p", { class: "twc-price" });
    this.messagesEl = el("div", { class: "twc-messages", "aria-live": "polite" });
    this.tabsEl = el("div", { class: "twc-tabs", role: "tablist", "aria-label": "Pasos de personalización" });
    this.panelEl = el("div", { class: "twc-panel", role: "tabpanel" });
    this.stepNavEl = el("div", { class: "twc-step-nav" });
    this.desktopCtaEl = el("div", { class: "twc-cta-desktop" });
    this.mobileCtaEl = el(
      "div",
      { class: "twc-cta-mobile" },
      [
        el("span", { class: "twc-cta-mobile-price" }),
        el("button", { type: "button", class: "twc-btn twc-btn-primary", onclick: () => this._handleRequestClick() }, "Pedir por WhatsApp"),
      ]
    );

    const header = el("div", { class: "twc-header" }, [
      this.previewEl,
      el("div", { class: "twc-price-row" }, [this.priceEl]),
      this.messagesEl,
    ]);

    this.appendChild(header);
    this.appendChild(this.tabsEl);
    this.appendChild(this.panelEl);
    this.appendChild(this.stepNavEl);
    this.appendChild(this.desktopCtaEl);
    this.appendChild(this.mobileCtaEl);

    // Capas visuales del preview: una por cada categoría visual del catálogo
    // completo (no sólo del modelo activo), ordenadas por layerZIndex.
    this.layerImgs = new Map();
    const visualCategories = [...this.catalog.categories]
      .filter((c) => c.isVisual)
      .sort((a, b) => a.layerZIndex - b.layerZIndex);
    for (const category of visualCategories) {
      const img = el("img", {
        class: "twc-layer",
        alt: "",
        loading: "eager",
        style: `z-index:${category.layerZIndex}`,
        hidden: true,
        "data-category": category.id,
      });
      this.layerImgs.set(category.id, img);
      this.previewEl.appendChild(img);
    }

    this.modal = this._buildConfirmationModal();
    this.appendChild(this.modal);
  }

  _renderAll() {
    this._renderPreviewLayers();
    this._renderPrice();
    this._renderTabs();
    this._renderPanel();
    this._renderStepNav();
    this._renderMessages();
    this._renderDesktopCta();
    this._updateCtaState();
  }

  /**
   * En desktop el CTA principal vive fuera del panel con tabs, siempre
   * visible debajo del resumen (ver layout en watch-configurator.css,
   * `.twc-cta-desktop`). En mobile se usa la barra sticky (`.twc-cta-mobile`).
   */
  _renderDesktopCta() {
    this.desktopCtaEl.innerHTML = "";
    // En la tab "Resumen" el propio panel ya trae su botón de pedido: no
    // duplicamos el CTA ahí, sólo actúa como atajo persistente en el resto
    // de las tabs (por si el usuario ya terminó y no quiere volver a Resumen).
    if (this.activeTab === RESUMEN_TAB_ID) {
      this.desktopCtaEl.hidden = true;
      return;
    }
    this.desktopCtaEl.hidden = false;
    const validation = this._validate();
    this.desktopCtaEl.appendChild(
      el(
        "button",
        {
          type: "button",
          class: "twc-btn twc-btn-primary twc-btn-block",
          disabled: validation.valid ? null : true,
          onclick: () => this._handleRequestClick(),
        },
        this.settings.ctaText || "Pedir por WhatsApp"
      )
    );
  }

  _renderPreviewLayers() {
    for (const [categoryId, img] of this.layerImgs.entries()) {
      const componentId = this.selections[categoryId];
      const component = componentId ? this.componentsById.get(componentId) : null;
      const url = component ? component.layerImage : null;
      if (url) {
        if (img.getAttribute("src") !== url) img.src = url;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }
  }

  _formattedPrice() {
    return formatPrice(this.settings.price, {
      template: this.settings.priceFormatTemplate,
      thousandsSeparator: this.settings.thousandsSeparator,
    });
  }

  /**
   * Seña sugerida (30% del precio total), redondeada al entero más cercano.
   * Se muestra en el mensaje de WhatsApp para que el pedido llegue con el
   * monto de seña ya calculado, sin que el cliente tenga que sacar la cuenta.
   */
  _formattedDeposit() {
    const depositAmount = Math.round((this.settings.price || 0) * DEPOSIT_PERCENTAGE);
    return formatPrice(depositAmount, {
      template: this.settings.priceFormatTemplate,
      thousandsSeparator: this.settings.thousandsSeparator,
    });
  }

  _renderPrice() {
    const formatted = this._formattedPrice();
    this.priceEl.textContent = formatted;
    const mobilePrice = this.mobileCtaEl.querySelector(".twc-cta-mobile-price");
    if (mobilePrice) mobilePrice.textContent = formatted;
  }

  _renderTabs() {
    this.tabsEl.innerHTML = "";
    const tabs = [];
    if ((this.catalog.models || []).length) {
      tabs.push({ id: MODEL_TAB_ID, label: "Modelo" });
    }
    for (const category of this._modelCategories()) {
      tabs.push({ id: category.id, label: category.name });
    }
    tabs.push({ id: RESUMEN_TAB_ID, label: "Resumen" });

    for (const tab of tabs) {
      const isActive = tab.id === this.activeTab;
      const isDone = tab.id !== MODEL_TAB_ID && tab.id !== RESUMEN_TAB_ID && !!this.selections[tab.id];
      const button = el(
        "button",
        {
          type: "button",
          role: "tab",
          id: `twc-tab-${tab.id}`,
          "aria-selected": isActive ? "true" : "false",
          "aria-controls": "twc-panel",
          class: `twc-tab${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`,
          onclick: () => this._setActiveTab(tab.id),
        },
        tab.label
      );
      this.tabsEl.appendChild(button);
    }
  }

  _renderPanel() {
    this.panelEl.innerHTML = "";
    this.panelEl.id = "twc-panel";
    this.panelEl.setAttribute("aria-labelledby", `twc-tab-${this.activeTab}`);

    if (this.activeTab === MODEL_TAB_ID) {
      this.panelEl.appendChild(this._renderModelPicker());
    } else if (this.activeTab === RESUMEN_TAB_ID) {
      this.panelEl.appendChild(this._renderSummary());
    } else {
      this.panelEl.appendChild(this._renderOptionsGrid(this.activeTab));
    }
  }

  _renderModelPicker() {
    const grid = el("div", { class: "twc-options-grid", role: "radiogroup", "aria-label": "Elegí tu modelo base" });
    for (const model of this.catalog.models || []) {
      if (model.active === false) continue;
      const isSelected = this.model && this.model.id === model.id;
      const card = el(
        "button",
        {
          type: "button",
          role: "radio",
          "aria-checked": isSelected ? "true" : "false",
          class: `twc-option twc-option--model${isSelected ? " is-selected" : ""}`,
          onclick: () => this._handleModelChange(model),
        },
        [
          el("span", { class: "twc-option-thumb-wrap" }, [el("img", { class: "twc-option-thumb", src: model.thumbnail, alt: "", loading: "lazy" })]),
          el("span", { class: "twc-option-name" }, model.name),
          model.description ? el("span", { class: "twc-option-desc" }, model.description) : null,
        ]
      );
      grid.appendChild(card);
    }
    return grid;
  }

  _renderOptionsGrid(categoryId) {
    const category = this.categoriesById.get(categoryId);
    const options = this.engine.getAvailableComponents(categoryId, this.selections, this._modelComponents());
    const grid = el("div", {
      class: "twc-options-grid",
      role: "radiogroup",
      "aria-label": `Elegí ${category ? category.name.toLowerCase() : "una opción"}`,
    });

    if (!options.length) {
      grid.appendChild(el("p", { class: "twc-empty" }, "No hay opciones cargadas para esta categoría todavía."));
      return grid;
    }

    for (const { component, availability, selectable, reason } of options) {
      const isSelected = this.selections[categoryId] === component.id;
      const statusLabel = STATUS_LABELS[availability];
      const card = el(
        "button",
        {
          type: "button",
          role: "radio",
          "aria-checked": isSelected ? "true" : "false",
          "aria-disabled": selectable ? null : "true",
          title: reason || undefined,
          class: `twc-option${isSelected ? " is-selected" : ""}${selectable ? "" : " is-disabled"}`,
          onclick: () => (selectable ? this._handleComponentSelect(component.id) : this._flashReason(reason)),
        },
        [
          el("span", { class: "twc-option-thumb-wrap" }, [
            component.thumbnail ? el("img", { class: "twc-option-thumb", src: component.thumbnail, alt: "", loading: "lazy" }) : null,
          ]),
          el("span", { class: "twc-option-name" }, component.name),
          statusLabel ? el("span", { class: `twc-badge twc-badge--${availability}` }, statusLabel) : null,
        ]
      );
      grid.appendChild(card);
    }
    return grid;
  }

  _renderSummary() {
    const wrap = el("div", { class: "twc-summary" });
    wrap.appendChild(el("h3", { class: "twc-summary-title" }, "Tu reloj"));

    if (this.model) {
      wrap.appendChild(this._summaryRow("Modelo", this.model.name));
    }
    for (const category of this._modelCategories()) {
      const componentId = this.selections[category.id];
      const component = componentId ? this.componentsById.get(componentId) : null;
      wrap.appendChild(this._summaryRow(category.name, component ? component.name : "Sin elegir"));
    }

    wrap.appendChild(el("p", { class: "twc-summary-price" }, `Precio: ${this._formattedPrice()}`));
    const delivery = this._effectiveDeliveryEstimate();
    if (delivery) {
      wrap.appendChild(el("p", { class: "twc-summary-delivery" }, `Entrega estimada: ${delivery}`));
    }

    const validation = this._validate();
    if (!validation.valid) {
      wrap.appendChild(
        el(
          "p",
          { class: "twc-summary-warning" },
          validation.missingCategories.length
            ? `Te falta elegir: ${validation.missingCategories.map((id) => (this.categoriesById.get(id) || {}).name || id).join(", ")}.`
            : "Hay una incompatibilidad pendiente de resolver."
        )
      );
    }

    wrap.appendChild(
      el(
        "button",
        {
          type: "button",
          class: "twc-btn twc-btn-primary twc-btn-block",
          disabled: validation.valid ? null : true,
          onclick: () => this._handleRequestClick(),
        },
        this.settings.ctaText || "Pedir por WhatsApp"
      )
    );

    return wrap;
  }

  /**
   * El tiempo de entrega general vive en Theme Settings, pero una pieza
   * puntual (ej. un movimiento GMT por pedido especial) puede pisar ese
   * valor con `leadTimeOverride`. Si hay una o más piezas seleccionadas con
   * override, se muestran esas en vez del valor general.
   */
  _effectiveDeliveryEstimate() {
    const overrides = Object.values(this.selections)
      .map((id) => this.componentsById.get(id))
      .filter((c) => c && c.leadTimeOverride);
    if (overrides.length) {
      return overrides.map((c) => `${c.leadTimeOverride} (${c.name})`).join(" · ");
    }
    return this.settings.deliveryEstimate || "";
  }

  _summaryRow(label, value) {
    return el("div", { class: "twc-summary-row" }, [
      el("span", { class: "twc-summary-label" }, label),
      el("span", { class: "twc-summary-value" }, value),
    ]);
  }

  _renderMessages() {
    this.messagesEl.innerHTML = "";
    for (const message of this.messages) {
      this.messagesEl.appendChild(el("p", { class: "twc-message" }, message));
    }
  }

  _updateCtaState() {
    const validation = this._validate();
    const button = this.mobileCtaEl.querySelector("button");
    if (button) button.disabled = !validation.valid;
  }

  // ------------------------------------------------------------- ACTIONS --

  _setActiveTab(tabId) {
    this.activeTab = tabId;
    this._renderTabs();
    this._renderPanel();
    this._scrollPanelIntoView();
  }

  _scrollPanelIntoView() {
    if (window.matchMedia("(max-width: 767px)").matches) {
      this.tabsEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  _handleModelChange(model) {
    if (this.model && this.model.id === model.id) return;
    this.model = model;
    this._configurationCompletedFired = false;
    this._applyDefaultSelectionsForModel();
    this._renderAll();
    this._preloadInitialAssets();
  }

  _handleComponentSelect(componentId) {
    const component = this.componentsById.get(componentId);
    if (!component) return;
    const previousId = this.selections[component.categoryId];
    const { selections, messages } = this.engine.applySelection(this.selections, componentId, this._modelComponents());
    this.selections = selections;
    this.messages = messages;

    trackEvent("component_selected", {
      categoryId: component.categoryId,
      componentId: component.id,
      previousComponentId: previousId || null,
    });

    this._renderAll();
    this._preloadNeighbors(component.categoryId);

    const validation = this._validate();
    if (validation.valid && !this._configurationCompletedFired) {
      this._configurationCompletedFired = true;
      trackEvent("configuration_completed", { modelId: this.model ? this.model.id : null, selections: this.selections });
    } else if (!validation.valid) {
      this._configurationCompletedFired = false;
    }

    if (messages.length) {
      window.clearTimeout(this._messagesTimeout);
      this._messagesTimeout = window.setTimeout(() => {
        this.messages = [];
        this._renderMessages();
      }, 6000);
    }
  }

  _flashReason(reason) {
    if (!reason) return;
    this.messages = [reason];
    this._renderMessages();
    window.clearTimeout(this._messagesTimeout);
    this._messagesTimeout = window.setTimeout(() => {
      this.messages = [];
      this._renderMessages();
    }, 4000);
  }

  /**
   * Orden lineal de pasos (Modelo -> categorías del modelo, en el orden del
   * metaobject -> Resumen). Lo usan tanto las tabs como las flechas
   * Anterior/Siguiente, para que ambas navegaciones sean consistentes.
   */
  _tabOrder() {
    const order = [MODEL_TAB_ID, ...this._modelCategories().map((c) => c.id), RESUMEN_TAB_ID];
    return order.filter((id) => (id === MODEL_TAB_ID ? (this.catalog.models || []).length > 0 : true));
  }

  _goToNextTab() {
    const order = this._tabOrder();
    const idx = order.indexOf(this.activeTab);
    if (idx === -1 || idx === order.length - 1) return;
    this._setActiveTab(order[idx + 1]);
  }

  _goToPreviousTab() {
    const order = this._tabOrder();
    const idx = order.indexOf(this.activeTab);
    if (idx <= 0) return;
    this._setActiveTab(order[idx - 1]);
  }

  /**
   * Barra con flechas "Anterior" / "Siguiente" debajo del panel. Elegir una
   * pieza NO avanza sola al siguiente paso -- el cliente se queda en la
   * categoría actual (por si quiere probar otra opción) y usa estas flechas,
   * o directamente las tabs de arriba, para moverse.
   */
  _renderStepNav() {
    this.stepNavEl.innerHTML = "";
    const order = this._tabOrder();
    const idx = order.indexOf(this.activeTab);
    if (idx === -1) {
      this.stepNavEl.hidden = true;
      return;
    }
    this.stepNavEl.hidden = false;
    this.stepNavEl.appendChild(
      el(
        "button",
        {
          type: "button",
          class: "twc-btn twc-btn-ghost twc-step-nav-btn twc-step-nav-prev",
          disabled: idx <= 0 ? true : null,
          onclick: () => this._goToPreviousTab(),
        },
        "‹ Anterior"
      )
    );
    this.stepNavEl.appendChild(
      el(
        "button",
        {
          type: "button",
          class: "twc-btn twc-btn-ghost twc-step-nav-btn twc-step-nav-next",
          disabled: idx >= order.length - 1 ? true : null,
          onclick: () => this._goToNextTab(),
        },
        "Siguiente ›"
      )
    );
  }

  _validate() {
    const requiredCategoryIds = this._modelCategories().map((c) => c.id);
    return this.engine.validateSelections(this.selections, requiredCategoryIds);
  }

  // ----------------------------------------------------------- WHATSAPP --

  async _handleRequestClick() {
    const validation = this._validate();
    if (!validation.valid) {
      this._setActiveTab(RESUMEN_TAB_ID);
      return;
    }

    trackEvent("whatsapp_order_started", { modelId: this.model ? this.model.id : null });
    this._openModal({ loading: true });

    try {
      const layers = [...this.layerImgs.entries()]
        .filter(([, img]) => !img.hidden && img.getAttribute("src"))
        .map(([categoryId, img]) => ({ url: img.getAttribute("src"), zIndex: this.categoriesById.get(categoryId).layerZIndex }));

      const blob = await this.compositor.composeToBlob(layers, { background: "white", size: 1200 });
      const configurationId = generateConfigurationId();
      trackEvent("watch_image_generated", { configurationId });

      this._pendingOrder = {
        configurationId,
        blob,
        imageUrl: URL.createObjectURL(blob),
      };
      this._openModal({ loading: false });
    } catch (err) {
      console.error("[TIME Configurator] Error generando la imagen final", err);
      this._closeModal();
      this._flashReason("No pudimos generar la imagen de tu reloj. Probá de nuevo.");
    }
  }

  _buildConfirmationModal() {
    const overlay = el("div", { class: "twc-modal-overlay", hidden: true, role: "presentation", onclick: (e) => {
      if (e.target === overlay) this._closeModal();
    } });
    const dialog = el("div", { class: "twc-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "twc-modal-title" });
    overlay.appendChild(dialog);
    this._modalDialog = dialog;
    this._modalOverlay = overlay;
    return overlay;
  }

  _openModal({ loading }) {
    const dialog = this._modalDialog;
    dialog.innerHTML = "";

    if (loading) {
      dialog.appendChild(el("p", { class: "twc-modal-loading" }, "Generando la imagen de tu reloj..."));
    } else {
      const { configurationId, imageUrl } = this._pendingOrder;
      dialog.appendChild(el("h3", { id: "twc-modal-title", class: "twc-modal-title" }, "Tu reloj está listo."));
      dialog.appendChild(el("img", { class: "twc-modal-image", src: imageUrl, alt: "Tu reloj personalizado" }));
      dialog.appendChild(el("p", { class: "twc-modal-config-id" }, `Configuración: ${configurationId}`));
      dialog.appendChild(el("p", { class: "twc-modal-price" }, `Precio: ${this._formattedPrice()}`));
      dialog.appendChild(el("p", { class: "twc-modal-deposit" }, `Seña (30%): ${this._formattedDeposit()}`));
      {
        const delivery = this._effectiveDeliveryEstimate();
        if (delivery) dialog.appendChild(el("p", { class: "twc-modal-delivery" }, `Entrega estimada: ${delivery}`));
      }
      dialog.appendChild(
        el(
          "button",
          { type: "button", class: "twc-btn twc-btn-primary twc-btn-block", onclick: () => this._confirmWhatsAppOrder() },
          "Continuar en WhatsApp"
        )
      );
      dialog.appendChild(
        el("button", { type: "button", class: "twc-btn twc-btn-ghost twc-btn-block", onclick: () => this._closeModal() }, "Seguir editando")
      );
    }

    this._modalOverlay.hidden = false;
    document.body.classList.add("twc-modal-open");
  }

  _closeModal() {
    this._modalOverlay.hidden = true;
    document.body.classList.remove("twc-modal-open");
    if (this._pendingOrder && this._pendingOrder.imageUrl) {
      URL.revokeObjectURL(this._pendingOrder.imageUrl);
    }
    this._pendingOrder = null;
  }

  async _confirmWhatsAppOrder() {
    if (!this._pendingOrder) return;
    const { configurationId, blob } = this._pendingOrder;

    const record = buildConfigurationRecord({
      configurationId,
      model: this.model,
      selections: this.selections,
      componentsById: this.componentsById,
      price: this.settings.price,
      currencyCode: this.settings.currencyCode,
    });

    const message = buildWhatsAppMessage({
      brandName: this.settings.brandName || "TIME",
      categoriesInOrder: this._modelCategories(),
      selections: this.selections,
      componentsById: this.componentsById,
      formattedPrice: this._formattedPrice(),
      formattedDeposit: this._formattedDeposit(),
      deliveryEstimate: this._effectiveDeliveryEstimate(),
      configurationId,
    });

    try {
      const result = await sendConfigurationToWhatsApp({
        imageBlob: blob,
        configurationId,
        whatsappNumber: this.settings.whatsappNumber,
        message,
      });
      if (result.opened) {
        trackEvent("whatsapp_clicked", { configurationId, method: result.method, record });
        this._closeModal();
      }
    } catch (err) {
      console.error("[TIME Configurator] Error abriendo WhatsApp", err);
      this._flashReason("No pudimos abrir WhatsApp. Intentá de nuevo.");
    }
  }

  // ---------------------------------------------------------- PRELOADING --

  _preloadInitialAssets() {
    const urls = [...this.layerImgs.values()].map((img) => img.getAttribute("src")).filter(Boolean);
    this.preloader.loadAll(urls);
    // Precarga de baja prioridad: el resto de las opciones de las
    // categorías del modelo activo, para que cambiar de pieza no tenga
    // que esperar la descarga de la imagen.
    const allLayerUrls = this._modelComponents().map((c) => c.layerImage).filter(Boolean);
    this.preloader.idlePreload(allLayerUrls);
  }

  _preloadNeighbors(categoryId) {
    const siblingUrls = this._modelComponents()
      .filter((c) => c.categoryId === categoryId)
      .map((c) => c.layerImage)
      .filter(Boolean);
    this.preloader.idlePreload(siblingUrls);
  }
}

if (!customElements.get("time-watch-configurator")) {
  customElements.define("time-watch-configurator", TimeWatchConfigurator);
}
